/**
 * @file ChallengeReadingContent.jsx
 * @description Reading content component that displays Bible text for MaterialChallenge
 */

import React, { useState, useEffect, useRef } from "react";
import { extractChapterTitle, formatChapterTitle, getProperBookName } from "../../../utils/bible_language_utils";
import { getBibleLanguageFromCookie } from "../../../utils/theme_utils";
import { BIBLE_LANGUAGES } from "../../../constants";
import { Icon } from "../../Icon";

export const ChallengeReadingContent = ({ readings, dayIndex, bibleCache, fetchBibleTextForDay, loadingDays, textSizes, bookmark, onVerseClick, readingContainerRef }) => {
  const cacheKey = `day-${dayIndex}`;
  const [contentState, setContentState] = useState(() => ({
    dayIndex,
    bibleText: bibleCache.get(cacheKey) || null,
    error: null
  }));
  const autoScrolledRef = useRef(false);
  const bibleText = contentState.dayIndex === dayIndex ? contentState.bibleText : null;
  const error = contentState.dayIndex === dayIndex ? contentState.error : null;

  useEffect(() => {
    let isCurrentDay = true;

    const loadBibleText = async () => {
      if (!readings || readings.length === 0) {
        setContentState({ dayIndex, bibleText: null, error: null });
        return;
      }

      if (bibleCache.has(cacheKey)) {
        setContentState({
          dayIndex,
          bibleText: bibleCache.get(cacheKey),
          error: null
        });
        return;
      }

      setContentState({ dayIndex, bibleText: null, error: null });

      try {
        const data = await fetchBibleTextForDay(dayIndex);
        if (!isCurrentDay) {
          return;
        }
        if (data) {
          setContentState({ dayIndex, bibleText: data, error: null });
        } else {
          setContentState({
            dayIndex,
            bibleText: null,
            error: 'Failed to load Bible text'
          });
        }
      } catch (err) {
        if (!isCurrentDay) {
          return;
        }
        console.error('Error loading Bible text:', err);
        setContentState({
          dayIndex,
          bibleText: null,
          error: err.message
        });
      }
    };

    loadBibleText();

    return () => {
      isCurrentDay = false;
    };
  }, [readings, dayIndex, bibleCache, cacheKey, fetchBibleTextForDay]);

  // Helper function to format verse text with italics for bracketed content
  const formatVerseText = (text) => {
    if (!text) return text;
    // Replace [text] with <em>text</em>
    return text.replace(/\[([^\]]+)\]/g, '<em>$1</em>');
  };

  // Helper function to group verses by chapter
  const groupVersesByChapter = (verses) => {
    // Get user's current Bible language preference
    const bibleLanguage = getBibleLanguageFromCookie() || BIBLE_LANGUAGES.ENGLISH;
    
    const chapters = {};
    verses.forEach(verse => {
      // Extract book and chapter from reference like "2 Kings 16:1", "ruth 3:3", etc.
      const refMatch = verse.ref.match(/^(.+?)\s+(\d+):(\d+)$/);
      if (refMatch) {
        const verseNum = refMatch[3];
        const chapterNum = parseInt(refMatch[2]);
        const bookName = getProperBookName(refMatch[1], bibleLanguage);
        
        // Use language-aware chapter title extraction
        // This handles Chinese refs (English book names -> Chinese characters)
        // and other language abbreviations properly
        const chapterKey = extractChapterTitle(verse.ref, bibleLanguage);
        
        if (!chapters[chapterKey]) {
          chapters[chapterKey] = [];
        }
        chapters[chapterKey].push({
          number: parseInt(verseNum),
          text: verse.text,
          book: bookName,
          chapter: chapterNum,
          referenceBook: refMatch[1]
        });
      }
    });
    
    // Sort verses within each chapter
    Object.keys(chapters).forEach(chapterKey => {
      chapters[chapterKey].sort((a, b) => a.number - b.number);
    });
    
    return chapters;
  };

  const getVerseId = (book, chapter, verse) => {
    const cleanBook = String(book || '').trim().replace(/\s+/g, '-');
    return `verse-${cleanBook}-${chapter}-${verse}`;
  };

  useEffect(() => {
    autoScrolledRef.current = false;
  }, [dayIndex, bookmark?.book, bookmark?.chapter, bookmark?.verse]);

  useEffect(() => {
    if (!bookmark || !bibleText || !bibleText.verses || autoScrolledRef.current) {
      return;
    }
    const container = readingContainerRef?.current;
    if (!container) {
      return;
    }
    const verseId = getVerseId(bookmark.book, bookmark.chapter, bookmark.verse);
    const element = document.getElementById(verseId);
    if (!element) {
      return;
    }
    autoScrolledRef.current = true;
    const animationFrame = requestAnimationFrame(() => {
      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const targetTop = elementRect.top - containerRect.top + container.scrollTop;
      const centeredTop = targetTop - (container.clientHeight / 2) + (elementRect.height / 2);
      const scrollTop = Math.max(centeredTop, 0);
      if (typeof container.scrollTo === 'function') {
        container.scrollTo({
          top: scrollTop,
          left: 0,
          behavior: 'auto'
        });
        return;
      }
      container.scrollTop = scrollTop;
      container.scrollLeft = 0;
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [bookmark, bibleText, dayIndex, readingContainerRef]);

  // Check if there are no readings for this day
  if (!readings || readings.length === 0) {
    return (
      <div style={{
        marginTop: '16px',
        padding: '12px',
        backgroundColor: 'var(--md-sys-color-surface-variant)',
        borderRadius: '8px',
        fontSize: '14px',
        color: 'var(--md-sys-color-on-surface-variant)',
        textAlign: 'center'
      }}>
        <Icon name="calendar" style={{ verticalAlign: 'middle', marginRight: '8px' }} />
        No readings for this day
      </div>
    );
  }

  const isLoading = loadingDays.has(dayIndex);

  if (isLoading && !bibleText) {
    return (
      <div style={{ color: 'var(--md-sys-color-on-surface)', textAlign: 'center', padding: '24px' }}>
        <md-circular-progress indeterminate></md-circular-progress>
        <p style={{ marginTop: '16px', color: 'var(--md-sys-color-on-surface-variant)' }}>
          Loading Bible text...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ color: 'var(--md-sys-color-on-surface)' }}>
        <div style={{
          marginTop: '16px',
          padding: '12px',
          backgroundColor: 'var(--md-sys-color-error-container)',
          borderRadius: '8px',
          fontSize: '14px',
          color: 'var(--md-sys-color-on-error-container)'
        }}>
          <Icon name="error" style={{ verticalAlign: 'middle', marginRight: '8px' }} />
          Failed to load Bible text: {error}
          <br />
          <small>Please try again later.</small>
        </div>
      </div>
    );
  }

  if (!bibleText || !bibleText.verses) {
    return (
      <div style={{
        marginTop: '16px',
        padding: '12px',
        backgroundColor: 'var(--md-sys-color-secondary-container)',
        borderRadius: '8px',
        fontSize: '14px',
        color: 'var(--md-sys-color-on-secondary-container)'
      }}>
        <Icon name="loadingBook" style={{ verticalAlign: 'middle', marginRight: '8px' }} />
        Bible text is being loaded...
      </div>
    );
  }

  const chapterGroups = groupVersesByChapter(bibleText.verses);
  const bibleLanguage = getBibleLanguageFromCookie() || BIBLE_LANGUAGES.ENGLISH;
  const isBookmarked = (verse) => {
    if (!bookmark || !verse) {
      return false;
    }
    return String(bookmark.book).toLowerCase() === String(verse.book).toLowerCase()
      && Number(bookmark.chapter) === Number(verse.chapter)
      && Number(bookmark.verse) === Number(verse.number);
  };

  return (
    <div style={{ color: 'var(--md-sys-color-on-surface)' }}>
      {Object.entries(chapterGroups).map(([chapterTitle, verses]) => {
        const formattedTitle = formatChapterTitle(chapterTitle, bibleLanguage);
        return (
        <div key={chapterTitle} style={{ marginBottom: '32px' }}>
          {/* Chapter heading */}
          <h3 style={{
            fontSize: textSizes.heading,
            fontWeight: '600',
            color: 'var(--md-sys-color-primary)',
            marginBottom: '16px',
            borderBottom: '2px solid var(--md-sys-color-primary)',
            paddingBottom: '4px'
          }}>
            {formattedTitle}
          </h3>
          
          {/* Numbered verse list */}
          <ol style={{
            paddingLeft: '20px',
            lineHeight: '1.8',
            fontSize: textSizes.body
          }}>
            {verses.map((verse, index) => {
              const verseId = getVerseId(verse.book, verse.chapter, verse.number);
              const bookmarked = isBookmarked(verse);
              return (
                <li 
                  key={index} 
                  style={{ 
                    marginBottom: '8px',
                    color: 'var(--md-sys-color-on-surface)',
                    backgroundColor: bookmarked ? 'var(--md-sys-color-secondary-container)' : 'transparent',
                    borderRadius: bookmarked ? '8px' : '0',
                    padding: bookmarked ? '4px 32px 4px 8px' : '0',
                    position: 'relative',
                    cursor: onVerseClick ? 'pointer' : 'default'
                  }}
                  id={verseId}
                  data-verse-id={verseId}
                  role={onVerseClick ? 'button' : undefined}
                  tabIndex={onVerseClick ? 0 : -1}
                  onClick={(event) => {
                    if (onVerseClick) {
                      onVerseClick({
                        book: verse.book,
                        chapter: verse.chapter,
                        verse: verse.number,
                        verseId,
                        referenceBook: verse.referenceBook,
                        text: verse.text
                      }, event.currentTarget);
                    }
                  }}
                  onKeyDown={(event) => {
                    if (!onVerseClick) {
                      return;
                    }
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onVerseClick({
                        book: verse.book,
                        chapter: verse.chapter,
                        verse: verse.number,
                        verseId,
                        referenceBook: verse.referenceBook,
                        text: verse.text
                      }, event.currentTarget);
                    }
                  }}
                >
                  {bookmarked && (
                    <span style={{ position: 'absolute', right: '8px', top: '6px' }}>
                      <Icon name="bookmark" />
                    </span>
                  )}
                  <span
                    dangerouslySetInnerHTML={{ __html: formatVerseText(verse.text) }}
                  />
                </li>
              );
            })}
          </ol>
        </div>
        );
      })}

      {/* Copyright notice */}
      <div style={{
        marginTop: '24px',
        padding: '12px',
        backgroundColor: 'var(--md-sys-color-surface-variant)',
        borderRadius: '8px',
        fontSize: '12px',
        color: 'var(--md-sys-color-on-surface-variant)'
      }}>
        {bibleText.copyright || 'Text from the Recovery Version of the Bible © Living Stream Ministry'}
      </div>
    </div>
  );
};
